import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import {
  Upload,
  Landmark,
  MapPin,
  FileSpreadsheet,
  PenTool,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const ModalUploadPlotsForm = () => {
    const server_app_api_php = window.SERVER_APP_API_PHP;
  const [uploadMethod, setUploadMethod] = useState('');
  const [businessLocationID, setBusinessLocationID] = useState('');
  const [plotArea, setPlotArea] = useState('');
  const [plotNumber, setPlotNumber] = useState('');
  const [plotPrice, setPlotPrice] = useState('');
  const [plotStatus, setPlotStatus] = useState('');
  const [excelPlots, setExcelPlots] = useState(null);
  const [businessLocations, setBusinessLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const userCompanyID = 1;
  const userBusinessLocationID = '';

  useEffect(() => {
    const formData = new FormData();
    formData.append("apiType", 'getBusinessLocationPlotsOnUploadForm');
    formData.append("companyID", userCompanyID);
    formData.append("location", userBusinessLocationID);
    
    axios.post(`${server_app_api_php}/server/index.php`, formData)
      .then(response => {
        setBusinessLocations(response.data);
      })
      .catch(error => {
        setError("Failed to load business locations");
      });
  }, []);

  const handleUploadMethodChange = (e) => {
    setUploadMethod(e.target.value);
  };

  const handleExcelFileChange = (e) => {
    setExcelPlots(e.target.files[0]);
  };

  const handleSingleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    const formData = new FormData();
    formData.append("apiType", 'registerPlots');
    formData.append("companyID", userCompanyID);
    formData.append("businessLocationID", userBusinessLocationID);
    formData.append("plotArea", plotArea);
    formData.append("plotNumber", plotNumber);
    formData.append("plotPrice", plotPrice);
    formData.append("plotStatus", plotStatus);
    formData.append("business_locationIDPlots", businessLocationID );

    axios.post(`${server_app_api_php}/server/index.php`, formData)
      .then(response => {
        setLoading(false);
        if (response.data === 'Successful') {
          setSuccess(true);
          setPlotArea('');
          setPlotNumber('');
          setPlotPrice('');
          setPlotStatus('');
          setUploadMethod('');
          setExcelPlots(null);
          setBusinessLocationID('');
          setTimeout(() => setSuccess(false), 3000);
        } else if (response.data === 'Failed') {
          setError('Failed to Upload. Contact Support Team');
        } else if (response.data === 'Duplicate') {
          setError('Failed to Upload. Duplicate Entry');
        }
      })
      .catch(error => {
        setLoading(false);
        setError('Network error. Please check your internet connection.');
      });
  };

  const handleExcelFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    const formData = new FormData();
    formData.append('apiType', 'registerPlotsExcel');
    formData.append('excelPlots', excelPlots);
    formData.append('companyID', userCompanyID);
    formData.append('businessLocationID', businessLocationID);

    axios.post(`${server_app_api_php}/server/index.php`, formData)
      .then(response => {
        setLoading(false);
        if (response.data === 'Upload Completed Successfully' || response.data === '') {
          setSuccess(true);
          setExcelPlots(null);
          setBusinessLocationID('');
          setTimeout(() => setSuccess(false), 3000);
        } else {
          setError(response.data);
        }
      })
      .catch(error => {
        setLoading(false);
        setError('Network error. Please check your internet connection.');
      });
  };

  return (
    <>
      <Header />
      <main className="lg:ml-64 pt-16 min-h-screen bg-gray-50">
        <div className="p-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-2">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                  <Landmark className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Register Plots</h1>
                  <p className="text-gray-600">Upload single plot details or bulk upload via Excel</p>
                </div>
              </div>
            </div>

            {/* Status Messages */}
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">Successfully uploaded!</span>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <span className="text-red-800 font-medium">{error}</span>
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Upload Method
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setUploadMethod('Single Form')}
                    className={`p-4 rounded-xl border-2 transition-all ${uploadMethod === 'Single Form' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <PenTool className="w-5 h-5 text-blue-600" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Single Form</p>
                        <p className="text-sm text-gray-500">Register one plot at a time</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setUploadMethod('Upload Excel File')}
                    className={`p-4 rounded-xl border-2 transition-all ${uploadMethod === 'Upload Excel File' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <FileSpreadsheet className="w-5 h-5 text-green-600" />
                      <div className="text-left">
                        <p className="font-medium text-gray-900">Excel Upload</p>
                        <p className="text-sm text-gray-500">Bulk upload via Excel file</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {uploadMethod === 'Upload Excel File' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Location
                    </label>
                    <select
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      value={businessLocationID}
                      onChange={(e) => setBusinessLocationID(e.target.value)}
                      required
                    >
                      <option value="">Select Business Location</option>
                      {businessLocations.map((location) => (
                        <option key={location.businessLocationID} value={location.businessLocationID}>
                          {location.businessLocationName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Excel File
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Drag & drop your Excel file here</p>
                      <p className="text-sm text-gray-500 mb-4">or</p>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleExcelFileChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      {excelPlots && (
                        <div className="mt-4 p-3 bg-green-50 rounded-lg">
                          <p className="text-green-800 font-medium">Selected: {excelPlots.name}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={handleExcelFormSubmit}
                    disabled={loading || !excelPlots || !businessLocationID}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-6 rounded-xl font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? 'Uploading...' : 'Upload Excel File'}
                  </button>
                </div>
              )}

              {uploadMethod === 'Single Form' && (
                <form onSubmit={handleSingleFormSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Location
                    </label>
                    <select
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      value={businessLocationID}
                      onChange={(e) => setBusinessLocationID(e.target.value)}
                    >
                      <option value="">Select Business Location</option>
                      {businessLocations.map((location) => (
                        <option key={location.businessLocationID} value={location.businessLocationID}>
                          {location.businessLocationName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plot Number
                      </label>
                      <input
                        type="text"
                        value={plotNumber}
                        onChange={(e) => setPlotNumber(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter plot number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plot Area (m²)
                      </label>
                      <input
                        type="text"
                        value={plotArea}
                        onChange={(e) => setPlotArea(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter plot area"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plot Price (ZMW)
                      </label>
                      <input
                        type="text"
                        value={plotPrice}
                        onChange={(e) => setPlotPrice(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter plot price"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plot Status
                      </label>
                      <select
                        value={plotStatus}
                        onChange={(e) => setPlotStatus(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="">Select status</option>
                        <option value="Available">Available</option>
                        <option value="Sold">Sold</option>
                        <option value="Reserved">Reserved</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !plotNumber || !plotArea || !plotPrice || !plotStatus || !businessLocationID}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-xl font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? 'Registering...' : 'Register Plot'}
                  </button>
                </form>
              )}

              {!uploadMethod && (
                <div className="text-center py-12">
                  <Landmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select an upload method</h3>
                  <p className="text-gray-500">Choose between single form or Excel upload to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalUploadPlotsForm;